<template>
  <div class="flex flex-col gap-4">
    <SelectField
      :name="`${mode}ResponsibleStationId`"
      :options="stationOptions"
      :initial-value="transfer[`${mode}ResponsibleStation`]?.id"
      class="w-full"
    />
    <div class="flex gap-2"><InfoIcon class="size-4 flex-shrink-0" />{{ t('warningText') }}</div>
    <div class="flex gap-4 self-end">
      <CVButton variant="default" :is-loading="isSavingAllocation" @click.prevent="handleSave">{{
        t('save')
      }}</CVButton>
      <CVButton @click.prevent="handleCancel">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import CVButton from '@/components/buttons/CVButton.vue';
import SelectField from '@/components/SelectField.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { getStationOfCarByDate } from '@/hooks/use-current-station-of-car';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useStations } from '@/queries/use-stations';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUpdateAllocation } from '@/queries/use-allocations.ts';
import { useForm } from 'vee-validate';
import { InfoIcon } from 'lucide-vue-next';

const props = defineProps<{
  transfer: Allocation;
  mode: 'pickup' | 'dropoff';
}>();

const emit = defineEmits<{
  (e: 'cancelled'): void;
  (e: 'saved'): void;
}>();

const { t } = useI18n();

const reverseMode = props.mode === 'pickup' ? 'dropoff' : 'pickup';

const { handleSubmit, resetForm } = useForm<{
  pickupResponsibleStationId?: string | null;
  dropoffResponsibleStationId?: string | null;
}>({
  initialValues: {
    [`${props.mode}ResponsibleStationId`]:
      props.transfer[`${props.mode}ResponsibleStation`]?.id ?? null,
  },
  keepValuesOnUnmount: true,
});

const { isPending: isSavingAllocation, mutateAsync: updateAllocation } = useUpdateAllocation();

const handleSave = handleSubmit(async (values) => {
  await updateAllocation({
    id: props.transfer.id,
    allocation: values,
  });
  resetForm({ values });
  emit('saved');
});

const handleCancel = () => {
  resetForm();
  emit('cancelled');
};

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);
const { getLocalizedField } = useLocalizedFields();

const stationOptions = computed(() =>
  stations.value
    ?.filter(
      (station) =>
        props.transfer[`${reverseMode}LocationType`] !== BookingLocationType.STATION ||
        station.id !== props.transfer[`${reverseMode}ResponsibleStation`]?.id,
    )
    .map((station) => {
      const isCarStation =
        station.id === getStationOfCarByDate(props.transfer.car, props.transfer.startDate).id;
      const suffix = isCarStation ? ` - ${t('stationOfCar')}` : '';
      return {
        label: getLocalizedField(station.info) + suffix,
        value: station.id,
      };
    }),
);
</script>

<i18n lang="json">
{
  "en": {
    "stationOfCar": "Station of Car",
    "warningText": "With changing the station, the transfer times will be automatically recalculated."
  },
  "de": {
    "stationOfCar": "Station des Autos",
    "warningText": "Mit Ändern der Station werden die Transferzeiten automatisch neu berechnet."
  },
  "es": {
    "stationOfCar": "Estación del vehículo",
    "warningText": "Al cambiar la estación, las horas del traslado se recalcularán automáticamente."
  }
}
</i18n>
